import { Location } from '@angular/common';
import { Component, OnInit, Inject, Input, Optional, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilsService } from 'src/app/shared/utils.service';
import { ActionModel } from '../../actions/action.model';
import { ActionsService } from '../../actions/actions.service';
import { FilesRemoveComponent } from 'src/app/files/files-remove/files-remove.component';
import { NetworkedUsersSelectorComponent } from 'src/app/shared/networked-users-selector/networked-users-selector.component';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { RBACService } from 'src/app/shared/rbac.service';
import moment from 'moment';
import {
  ClientSitesSelectorComponent
} from "../../client/client-sites-selector/client-sites-selector.component";
import {SiteModel} from "../../models/site.model";

@Component({
    selector: 'app-user-tasks-edit',
    templateUrl: './user-tasks-edit.component.html',
    styleUrls: ['./user-tasks-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserTasksEditComponent implements OnInit {
  @Input() action: ActionModel;
  @Input() show_title = true;

  priorities: string[] = ['Low', 'Medium', 'High', 'Critical'];

  currentUserId: number;

  types = this.utils.getActionTypes();

  nonSystemOrigins = this.utils.getActionNonSystemOrigins();

  systemOrigins = this.utils.getActionSystemOrigins();

  isSystemOrigin = false;

  now: Date = new Date();

  selectedFiles: File[] = [];

  inProgress: boolean = false;

  selectedDate: moment.Moment;

  // RBAC permission flag
  canViewNetworkedUsers: boolean = false;

  constructor(
    private app: AppService,
    public location: Location,
    public utils: UtilsService,
    private actionsService: ActionsService,
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: ActionModel,
    private dialogRef: MatDialogRef<UserTasksEditComponent>,
    private api: ApiRequestService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    if (!this.action && this.dialogData) {
      this.action = this.dialogData;
    }

    this.isSystemOrigin = this.systemOrigins.includes(this.action?.origin);

    // Resolve RBAC permission flag.
    this.canViewNetworkedUsers = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_NETWORKED_USERS, RBACService.RBAC_A.A_VIEW)
    ], true);

    if ( !this.action.id ) {
      this.action.priority = 'Medium';

      // Auto-select current user as assignee.
      this.action.user_ids = [this.app.user.id];

      return;
    }
    this.find();
  }

  /**
   * Selects files to be processed.
   *
   * @param {any[]} files - An array of files to be selected.
   * @return {void}
   */
  onSelectFiles(files?: any[]): void {
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  onDateChange(value) {
    this.selectedDate = value;
  }

  /**
   * Send the API request to create or update a task.
   */
  onSubmit() {
    // Make sure the task is assigned to users.
    if (this.action.user_ids == null || !this.action.user_ids.length) {
      this.utils.showModal('Assignees Required', 'Please add at least one ' + this.utils.getLangTerm('employees.singular', 'Employee') + ' as an assignee');
      return;
    }

    if ( this.inProgress ) {
      return;
    }

    this.inProgress = true;

    // Check if a due date was selected before attempting to use formatting functions.
    if ( this.selectedDate ) {
      this.action.due = this.selectedDate.format('YYYY-MM-DD');
    }

    // If the id is present we want to send an update request, otherwise a create request.
    if ( this.action.id ) {
      this.update();
    } else {
      this.create();
    }
  }

  /**
   * Find a task.
   * @private
   */
  private find() {
    this.actionsService.find(this.action.id).subscribe((result) => {
      this.action = result.data;
      this.action.user_ids = this.action.users.map((user) => user.id);
    });
  }

  /**
   * Remove files from the task.
   */
  onRemoveFiles() {
    const dialogRef = this.dialog.open(FilesRemoveComponent, {
      data: this.action.files,
      width: '700px'
    });
    dialogRef.afterClosed().subscribe(() => this.find());
  }

  /**
   * Select users to assign to this task.
   */
  selectAssignee() {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.action.user_ids,
        multiple: true,
        selectedAccountId: this.app.account.id
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) { return; }
        this.action.user_ids = results;
      }
    );
  }

  /**
   * Select a site from the list of accessible sites the user has access to.
   */
  onSelectSite() {
    // Open the accessible sites' selector.
    this.utils.showComponentDialog(ClientSitesSelectorComponent, {
      selected: [this.action.site_id],
      multiple: false,
      return_object: false
    }, {
      width: '1200px'
    }).then((response: SiteModel | number | number[]) => {
      // Validate the selection response.
      if (typeof response !== 'number') { return; }
      this.action.site_id = response;
    });
  }

  /**
   * Remove the task.
   */
  delete() {
    this.utils.showModal(
      'Remove Actions',
      `Are you sure you want to remove "${this.action.name}"?`,
      () => {
        this.actionsService.remove([this.action.id]).subscribe(() => {
          this.utils.showToast(`"${this.action.name}" has been deleted.`);
          this.dialogRef.close(true);
        });
      }
    );
  }

  /**
   * Update the task.
   * @private
   */
  private update(): void {
    this.api
      .makeRequest('put', `v1/actions/${this.action.id}`, this.action)
      .then((response: { data: ActionModel }): void => {
        this.action = response.data;
        this.onSuccess(`Updated: "${response.data.name}"`, false);
        this.uploadSelectedFiles().then((): void => {
          this.find();
        });
      })
      .finally((): boolean => (this.inProgress = false));
  }

  /**
   * Create a new task.
   * @private
   * @return {void}
   */
  private create(): void {
    this.api
      .makeRequest('post', 'v1/actions', this.action)
      .then((response: { data: ActionModel; meta: {total: number} }): void => {
        this.action.id = response.data.id;
        this.onSuccess(`Created ${response.meta.total} Task(s)`);
        this.uploadSelectedFiles(response.data.related_action_ids);
      })
      .finally((): boolean => this.inProgress = false);
  }

  /**
   * Method to upload the selected files to the server.
   *
   * @private
   * @return {Promise<any>} A promise that resolves to a boolean value indicating whether the files were successfully uploaded.
   */
  private uploadSelectedFiles(related_action_ids: number[] = []): Promise<any> {
    return new Promise((resolve): void => {
      if ( this.selectedFiles.length > 0 ) {
        if ( related_action_ids.length > 0 ) {
          let promises: Promise<any>[] = [];
          related_action_ids.forEach((related_action_id: number): void => {
            promises.push(this.api.makeUploadRequest(`v2/file-manager/action/${related_action_id}`, this.selectedFiles));
          })
          Promise.all(promises)
            .then((): void => {
              this.utils.showToast('Your files successfully uploaded.');
              resolve(true);
            })
            .catch((e: Error): void => {
              resolve(false);
            })
            .finally((): void => {
              this.selectedFiles.length = 0;
            });
        } else {
          this.api.makeUploadRequest(`v2/file-manager/action/${this.action.id}`, this.selectedFiles)
            .then((response): void => {
              this.utils.showToast('Your files successfully uploaded.');
              resolve(true);
            })
            .catch((e: Error): void => {
              resolve(false);
            })
            .finally((): void => {
              this.selectedFiles.length = 0;
            });
        }
      } else {
        resolve(false);
      }
    });
  }

  /**
   * Handle the success of the API request.
   * @param message
   * @param shouldCloseDialog
   * @private
   */
  private onSuccess(message: string, shouldCloseDialog: boolean = true): void {
    this.utils.showToast(message);
    if ( shouldCloseDialog ) {
      this.dialogRef.close(true);
    }
  }

  protected readonly RBACService = RBACService;
}
